'use client';

import { faClock } from '@fortawesome/free-solid-svg-icons';
import { isAfter } from 'date-fns';
import React, { useState } from 'react';
import { utilsStyles } from '../../../..';
import FaIcon from '../../../../components/icon/FaIcon/FaIcon';
import CarouselModal from '../../../../components/ui/CarouselModal/CarouselModal';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import { newDate } from '../../../../utils/date';
import type { WChanceEventItem } from '../../types';
import { wchanceLabelStatus } from '../../utils';
import WChanceCardHeader from '../WChanceCardHeader/WChanceCardHeader';
import styles from './WChanceCard.module.scss';

export interface WChanceCardProps {
  wChanceEvents: Array<WChanceEventItem>;
  hasDetail?: boolean;
  hasMultiLabel?: boolean;
  wChanceDescription?: React.ReactNode;

  // 外部依存関数・定数
  formatDateCommon: (date: string, format: string) => string;
  Format: { dateTimeLotteryJa: string };
  WCHANCE_LABEL_STATUS: {
    sold_out: { text: string };
  };
  // ラベルステータス設定
  labelStatusConfig: {
    is_session: { text: string; color: string };
    soon_start: { text: string; color: string };
    finish: { text: string; color: string };
    sold_out: { text: string; color: string };
  };
}

function WChanceCard({
  wChanceEvents,
  hasDetail = false,
  hasMultiLabel = false,
  wChanceDescription,

  formatDateCommon,
  Format,
  WCHANCE_LABEL_STATUS,
  labelStatusConfig,
}: WChanceCardProps) {
  const [isScrollModalOpen, setIsScrollModalOpen] = useState(false);
  const [initShowImageUrlIndex, setInitShowImageUrlIndex] = useState(0);

  const openEventModal = (e: React.MouseEvent, event: WChanceEventItem) => {
    e.preventDefault();
    const clickedIndex = wChanceEvents.findIndex((item) => item.id === event.id);
    setInitShowImageUrlIndex(clickedIndex);
    setIsScrollModalOpen(true);
  };

  const eventIsFinish = (event: WChanceEventItem, labelStatusText: string) =>
    isAfter(newDate(), newDate(event.endAt)) || labelStatusText === WCHANCE_LABEL_STATUS.sold_out.text;

  const statusLabelStyle = (color: string) => {
    return {
      backgroundColor: `var(--color-${color})`,
    };
  };

  return (
    <div className={styles.componentStyle} data-testid='wchance-card'>
      <WChanceCardHeader showHeaderDescription={true} />
      {wChanceEvents.map((event) => {
        const labelStatus = wchanceLabelStatus(
          {
            startAt: event.startAt,
            endAt: event.endAt,
            hasStock: event.hasStock,
          },
          labelStatusConfig,
        );
        return (
          <React.Fragment key={event.id}>
            <hr className={styles.hr} />
            <div className={styles.eventItem}>
              {eventIsFinish(event, labelStatus.text) && <div className={styles.mask} />}
              <div className={styles.statusWrapper}>
                {hasMultiLabel ? (
                  <div className={styles.orderText}>第{event.sort}弾</div>
                ) : (
                  // 表示調整用のダミー要素
                  <div />
                )}
                <div
                  className={styles.statusLabel}
                  style={statusLabelStyle(labelStatus.color)}
                  data-testid='lottery-status-label'
                >
                  {labelStatus.text}
                </div>
              </div>
              <div className={`${styles.dateWrapper} ${utilsStyles.textBold}`}>
                <div className={styles.dateIcon}>
                  <FaIcon icon={faClock} />
                </div>
                <span>
                  &nbsp;
                  {formatDateCommon(event.startAt, Format.dateTimeLotteryJa)}
                  &nbsp;~&nbsp;
                  {formatDateCommon(event.endAt, Format.dateTimeLotteryJa)}
                </span>
              </div>
              <div className={styles.title}>{event.name}</div>
              {hasDetail && (
                <div className={styles.detailArea}>
                  <div className={styles.thumbnail}>
                    {/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
                    <a onClick={(e) => openEventModal(e, event)}>
                      <ImageView src={event.imageUrl} />
                    </a>
                  </div>
                  <p className={styles.labelDescription}>{event.description}</p>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
      {wChanceDescription && (
        <div className={styles.wChanceTypeDescription}>
          <span>Wチャンス賞について</span>
          <div
            className={`${utilsStyles.textPrewrap} ${utilsStyles.textHtml}`}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from API
            dangerouslySetInnerHTML={{ __html: wChanceDescription as string }}
          />
        </div>
      )}
      <CarouselModal
        initShowIndex={initShowImageUrlIndex}
        isOpen={isScrollModalOpen}
        close={() => setIsScrollModalOpen(false)}
        childrenArr={wChanceEvents.map((event) => (
          <div className={styles.goodsModal} key={event.id}>
            <ImageView src={event.imageUrl} alt={'Wチャンス景品'} isFreeSize />
            <div className={styles.modalPadding}>
              <div className={styles.marginTop}>
                <em>{event.name}</em>
              </div>
            </div>
          </div>
        ))}
      />
    </div>
  );
}

export default WChanceCard;
